import React, { useCallback, useEffect, useState } from "react";
import { BadgeCheck, DownloadCloud, Link2, Link2Off, MessageCircle, Phone, Plus, RefreshCw, RotateCcw, Save, Trash2 } from "lucide-react";
import {
  assignTenantNumber,
  confirmAgentCallerId,
  connectTenantWhatsApp,
  createTenantSubaccount,
  fetchTenantNumbers,
  fetchTenantSalesAgents,
  getTenantWhatsApp,
  provisionTenantNumber,
  releaseTenantNumber,
  searchAvailableNumbers,
  resetAgentCallerId,
  syncTenantNumbers,
  unassignTenantNumber,
  updateTenant,
  verifyAgentCallerId,
  type AvailableNumber,
  type TenantNumber,
  type TenantSalesAgent,
  type TenantWhatsAppIntegration
} from "../../services/api";
import { isMetaSignupConfigured, launchCoexistenceSignup } from "../../services/metaEmbeddedSignup";
import { useToast } from "../../components/ToastProvider";
import type { Tenant } from "../../types";

interface Props {
  tenant: Tenant;
}

/**
 * Operator-side Twilio number management for one tenant: attach numbers from
 * the tenant's Twilio subaccount and assign/unassign them to sales agents.
 * This is THE place numbers get wired up — the customer app deliberately has
 * no Twilio controls (customers verify agent mobiles, we manage inventory).
 */
export function NumbersTab({ tenant }: Props) {
  const toast = useToast();
  const [numbers, setNumbers] = useState<TenantNumber[]>([]);
  const [agents, setAgents] = useState<TenantSalesAgent[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [agentsError, setAgentsError] = useState<string | null>(null);
  const [syncing, setSyncing] = useState(false);
  // Buying a number. `available === null` means "not searched yet", which is a
  // different state from "searched and found nothing" and reads differently.
  const [buying, setBuying] = useState(false);
  const [searching, setSearching] = useState(false);
  const [purchasing, setPurchasing] = useState<string | null>(null);
  const [available, setAvailable] = useState<AvailableNumber[] | null>(null);
  const [buyCountry, setBuyCountry] = useState((tenant.country || "ZA").toUpperCase());
  const [buyAreaCode, setBuyAreaCode] = useState("");
  const [buyForwardTo, setBuyForwardTo] = useState("");
  const [buyError, setBuyError] = useState<string | null>(null);
  const [busyNumber, setBusyNumber] = useState<string | null>(null);
  const [verifying, setVerifying] = useState<string | null>(null);
  const [resetting, setResetting] = useState<string | null>(null);
  const [subaccountSid, setSubaccountSid] = useState(tenant.twilioSubaccountSid ?? "");
  const [savingSid, setSavingSid] = useState(false);
  const [creatingSub, setCreatingSub] = useState(false);

  // WhatsApp (Coexistence) state.
  const whatsappConfigured = isMetaSignupConfigured();
  const [waIntegrations, setWaIntegrations] = useState<TenantWhatsAppIntegration[]>([]);
  const [waAgentId, setWaAgentId] = useState("");
  const [connectingWa, setConnectingWa] = useState(false);

  const onCreateSubaccount = async () => {
    setCreatingSub(true);
    try {
      const res = await createTenantSubaccount(tenant.id);
      setSubaccountSid(res.subaccount.sid);
      toast.push(
        "success",
        `Subaccount ${res.subaccount.sid}${res.subaccount.mock ? " (mock)" : ""} created and linked.`
      );
    } catch (err) {
      toast.push("error", err instanceof Error ? err.message : "Could not create the subaccount.");
    } finally {
      setCreatingSub(false);
    }
  };

  const onSaveSubaccount = async () => {
    const sid = subaccountSid.trim();
    if (sid && !/^AC[0-9a-fA-F]{32}$/.test(sid)) {
      toast.push("error", "That doesn't look like a Twilio account SID (AC + 32 hex chars).");
      return;
    }
    setSavingSid(true);
    try {
      await updateTenant(tenant.id, { twilioSubaccountSid: sid || null });
      toast.push("success", sid ? "Twilio subaccount linked." : "Twilio subaccount cleared.");
    } catch (err) {
      toast.push("error", err instanceof Error ? err.message : "Could not save subaccount SID.");
    } finally {
      setSavingSid(false);
    }
  };

  const reload = useCallback(async (opts: { sync?: boolean } = {}) => {
    setLoading(true);
    try {
      // Auto-pickup: pull any numbers bought in the tenant's Twilio subaccount
      // (and wire their webhooks) before listing, so a number bought in Twilio
      // just appears here. Best-effort — never let it block showing the list.
      if (opts.sync) {
        try {
          await syncTenantNumbers(tenant.id);
        } catch {
          /* ignore: still show whatever is already attached */
        }
      }
      const [n, a, wa] = await Promise.all([
        fetchTenantNumbers(tenant.id),
        fetchTenantSalesAgents(tenant.id)
          .then((res) => {
            setAgentsError(null);
            return res;
          })
          .catch((err: unknown) => {
            // Don't silently swallow a failed rep load as "no reps" — an empty
            // dropdown then looks identical to a tenant with zero agents and
            // hides the real cause (a just-added rep "not showing up" when the
            // fetch actually errored). Surface it so the operator can retry.
            setAgentsError(err instanceof Error ? err.message : "Could not load sales reps.");
            return { agents: [] as TenantSalesAgent[] };
          }),
        getTenantWhatsApp(tenant.id).catch(() => ({ integrations: [] as TenantWhatsAppIntegration[] }))
      ]);
      setNumbers(n.numbers ?? []);
      // Deactivated reps can't take calls, but invited / on-leave reps are still
      // valid assignment targets (e.g. wiring up a number before the rep accepts
      // their invite), so only drop inactive ones from the picker.
      setAgents((a.agents ?? []).filter((x) => x.status !== "inactive"));
      setWaIntegrations(wa.integrations ?? []);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not load numbers.");
    } finally {
      setLoading(false);
    }
  }, [tenant.id]);

  useEffect(() => {
    void reload({ sync: true });
  }, [reload]);

  const onAssign = async (num: TenantNumber, agentId: string) => {
    setBusyNumber(num.externalId);
    try {
      if (agentId === "") {
        await unassignTenantNumber(tenant.id, num.externalId);
        toast.push("success", `${num.externalId} unassigned.`);
      } else {
        await assignTenantNumber(tenant.id, num.externalId, agentId);
        const agent = agents.find((a) => a.id === agentId);
        toast.push("success", `${num.externalId} → ${agent?.fullName ?? "agent"}.`);
      }
      await reload();
    } catch (err) {
      toast.push("error", err instanceof Error ? err.message : "Assignment failed.");
    } finally {
      setBusyNumber(null);
    }
  };

  const onSearch = async () => {
    setSearching(true);
    setBuyError(null);
    try {
      const res = await searchAvailableNumbers(tenant.id, {
        country: buyCountry || undefined,
        areaCode: buyAreaCode || undefined
      });
      setAvailable(res.numbers ?? []);
    } catch (e) {
      setBuyError(e instanceof Error ? e.message : "Could not search Twilio for numbers.");
      setAvailable(null);
    } finally {
      setSearching(false);
    }
  };

  const onBuy = async (phoneNumber: string) => {
    // Spending money on a recurring basis, so make the operator say yes to the
    // specific number rather than the row they think they clicked.
    if (!window.confirm(
      `Buy ${phoneNumber} for ${tenant.name}?\n\nThis purchases the number on Twilio and starts a ` +
      `recurring monthly cost. It will be wired to the voice webhook and routed immediately.`
    )) return;
    setPurchasing(phoneNumber);
    setBuyError(null);
    try {
      await provisionTenantNumber(tenant.id, {
        number: phoneNumber,
        country: buyCountry || undefined,
        forwardTo: buyForwardTo.trim() || undefined
      });
      toast.push("success", `Bought ${phoneNumber} and wired it up. Point it at an agent below.`);
      setBuying(false);
      setAvailable(null);
      await reload();
    } catch (e) {
      setBuyError(e instanceof Error ? e.message : "Could not buy that number.");
    } finally {
      setPurchasing(null);
    }
  };

  const onSync = async () => {
    setSyncing(true);
    try {
      const res = await syncTenantNumbers(tenant.id);
      if (res.note) {
        toast.push("success", res.note);
      } else if (res.synced?.length) {
        const wired = res.configured?.length
          ? `, wired ${res.configured.length} webhook${res.configured.length > 1 ? "s" : ""}`
          : "";
        toast.push(
          "success",
          `Picked up ${res.synced.length} number${res.synced.length > 1 ? "s" : ""} from Twilio${wired}.`
        );
      } else {
        toast.push("success", "No new numbers found in the Twilio subaccount.");
      }
      await reload();
    } catch (err) {
      toast.push("error", err instanceof Error ? err.message : "Sync from Twilio failed.");
    } finally {
      setSyncing(false);
    }
  };

  const onDelete = async (num: TenantNumber) => {
    const ok = window.confirm(
      `Release ${num.externalId}? This deletes it from the Twilio subaccount (you lose the number and stop being billed for it) and removes it from ${tenant.name}.`
    );
    if (!ok) return;
    setBusyNumber(num.externalId);
    try {
      await releaseTenantNumber(tenant.id, num.externalId);
      toast.push("success", `${num.externalId} released.`);
      await reload();
    } catch (err) {
      toast.push("error", err instanceof Error ? err.message : "Could not release the number.");
    } finally {
      setBusyNumber(null);
    }
  };

  const onVerifyCallerId = async (agent: TenantSalesAgent) => {
    if (!agent.phoneE164) {
      toast.push("error", "This rep has no mobile number set.");
      return;
    }
    setVerifying(agent.id);
    try {
      const start = await verifyAgentCallerId(tenant.id, agent.id);
      if (start.alreadyVerified || start.verified) {
        // Already a verified caller ID on Twilio — the server stamped it; no
        // code to key in. Just report success and refresh.
        toast.push("success", `${agent.fullName}'s number is already verified. Customers will see it on calls.`);
        await reload();
        setVerifying(null);
        return;
      }
      const entered = window.confirm(
        `Twilio is now calling ${agent.fullName} on ${agent.phoneE164}.\n\nHave them answer and enter this code on the keypad:\n\n${start.validationCode}\n\nClick OK once they have entered it.`
      );
      if (!entered) {
        setVerifying(null);
        return;
      }
      const { verified } = await confirmAgentCallerId(tenant.id, agent.id);
      if (verified) {
        toast.push("success", `${agent.fullName}'s number is verified. Customers will see it on calls.`);
      } else {
        toast.push("error", "Not verified yet. Make sure the code was entered, then try again.");
      }
      await reload();
    } catch (err) {
      toast.push("error", err instanceof Error ? err.message : "Caller-ID verification failed.");
    } finally {
      setVerifying(null);
    }
  };

  const onResetCallerId = async (agent: TenantSalesAgent) => {
    const ok = window.confirm(
      `Reset caller-ID verification for ${agent.fullName}?\n\nThis removes their number from the Twilio subaccount's verified caller IDs and clears the verified flag, so you can run the Verify flow again from scratch. It does NOT touch any purchased Twilio numbers.`
    );
    if (!ok) return;
    setResetting(agent.id);
    try {
      await resetAgentCallerId(tenant.id, agent.id);
      toast.push("success", `${agent.fullName}'s caller-ID verification was reset. Hit "Verify caller ID" to test it again.`);
      await reload();
    } catch (err) {
      toast.push("error", err instanceof Error ? err.message : "Could not reset caller-ID verification.");
    } finally {
      setResetting(null);
    }
  };

  const onConnectWhatsApp = async () => {
    if (!waAgentId) {
      toast.push("error", "Pick the sales agent whose WhatsApp number you're connecting.");
      return;
    }
    setConnectingWa(true);
    try {
      // 1) Meta's in-browser Embedded Signup (Coexistence) → { code, wabaId }.
      const { code, wabaId } = await launchCoexistenceSignup();
      // 2) Hand the result to our backend, which exchanges the code, registers
      //    the number and wires it to this agent.
      const res = await connectTenantWhatsApp(tenant.id, { agentId: waAgentId, code, wabaId });
      const agent = agents.find((a) => a.id === waAgentId);
      toast.push(
        "success",
        `WhatsApp ${res.displayPhoneNumber} connected${agent ? ` for ${agent.fullName}` : ""}.`
      );
      setWaAgentId("");
      await reload();
    } catch (err) {
      toast.push("error", err instanceof Error ? err.message : "Could not connect WhatsApp.");
    } finally {
      setConnectingWa(false);
    }
  };

  const voiceNumbers = numbers.filter((n) => n.channel === "voice");
  const otherEndpoints = numbers.filter((n) => n.channel !== "voice");

  return (
    <div className="card" style={{ display: "grid", gap: 16 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <div>
          <h3 style={{ margin: 0, display: "flex", alignItems: "center", gap: 8 }}>
            <Phone size={16} /> Twilio numbers
          </h3>
          <p className="muted" style={{ margin: "4px 0 0" }}>
            Buy a number here and it is purchased, webhook-wired and routed in one step. Numbers
            bought directly in the Twilio console still show up via Sync. Point each one at a sales
            agent. Operator-only.
          </p>
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          <button
            className="btn"
            onClick={() => {
              setBuying((v) => !v);
              setBuyError(null);
            }}
            disabled={loading}
            title="Search Twilio for a number and buy it for this tenant"
          >
            <Plus size={14} /> Buy a number
          </button>
          <button
            className="btn ghost"
            onClick={() => void onSync()}
            disabled={syncing || loading}
            title="Pull numbers from this tenant's Twilio subaccount and wire their webhooks"
          >
            <DownloadCloud size={14} /> {syncing ? "Syncing..." : "Sync from Twilio"}
          </button>
          <button className="btn ghost" onClick={() => void reload()} disabled={loading}>
            <RefreshCw size={14} /> Refresh
          </button>
        </div>
      </div>

      {buying ? (
        <div style={{ display: "grid", gap: 10, padding: 12, border: "1px solid var(--border,#e2e2e2)", borderRadius: 8 }}>
          <div style={{ display: "flex", gap: 8, alignItems: "flex-end", flexWrap: "wrap" }}>
            <div style={{ display: "grid", gap: 4 }}>
              <label className="muted" style={{ fontSize: 12 }}>Country</label>
              <input
                value={buyCountry}
                onChange={(e) => setBuyCountry(e.target.value.toUpperCase().slice(0, 2))}
                style={{ width: 70 }}
              />
            </div>
            <div style={{ display: "grid", gap: 4 }}>
              <label className="muted" style={{ fontSize: 12 }}>Area code (optional)</label>
              <input
                value={buyAreaCode}
                onChange={(e) => setBuyAreaCode(e.target.value.replace(/\D/g, "").slice(0, 4))}
                placeholder="21"
                style={{ width: 110 }}
              />
            </div>
            <div style={{ display: "grid", gap: 4, flex: 1, minWidth: 200 }}>
              <label className="muted" style={{ fontSize: 12 }}>Ring this phone (optional)</label>
              <input
                value={buyForwardTo}
                onChange={(e) => setBuyForwardTo(e.target.value)}
                placeholder="+27821234567"
              />
            </div>
            <button className="btn ghost" onClick={() => void onSearch()} disabled={searching}>
              {searching ? "Searching..." : "Search"}
            </button>
          </div>

          {buyError ? <p className="error" style={{ margin: 0 }}>{buyError}</p> : null}

          {available === null ? (
            <p className="muted" style={{ margin: 0, fontSize: 12 }}>
              Search first. Buying commits this tenant to a recurring monthly Twilio cost, and the
              number is wired to the voice webhook and routed the moment it is bought.
            </p>
          ) : available.length === 0 ? (
            <p className="muted" style={{ margin: 0 }}>
              No numbers available for {buyCountry}
              {buyAreaCode ? ` area ${buyAreaCode}` : ""}. Try a different area code.
            </p>
          ) : (
            <div style={{ display: "grid", gap: 6, maxHeight: 260, overflowY: "auto" }}>
              {available.map((n) => (
                <div
                  key={n.phoneNumber}
                  style={{
                    display: "flex", justifyContent: "space-between", alignItems: "center",
                    gap: 8, padding: "6px 8px", border: "1px solid var(--border,#eee)", borderRadius: 6
                  }}
                >
                  <div>
                    <strong>{n.phoneNumber}</strong>
                    <span className="muted" style={{ fontSize: 12 }}>
                      {n.locality ? ` · ${n.locality}` : ""}{n.region ? `, ${n.region}` : ""}
                    </span>
                  </div>
                  <button
                    className="btn"
                    disabled={!!purchasing}
                    onClick={() => void onBuy(n.phoneNumber)}
                  >
                    {purchasing === n.phoneNumber ? "Buying..." : "Buy"}
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      ) : null}

      <div style={{ display: "grid", gap: 6 }}>
        <label className="muted" style={{ fontSize: 12 }}>
          Twilio subaccount SID — every company gets its own subaccount so usage and numbers stay
          separate. Create it in the Twilio console, paste the AC... SID here.
        </label>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          <input
            style={{ flex: 1, minWidth: 280, fontFamily: "monospace" }}
            value={subaccountSid}
            onChange={(e) => setSubaccountSid(e.target.value)}
            placeholder="ACxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx"
            aria-label="Twilio subaccount SID"
          />
          <button
            className="btn"
            type="button"
            onClick={() => void onSaveSubaccount()}
            disabled={savingSid || (tenant.twilioSubaccountSid ?? "") === subaccountSid.trim()}
          >
            <Save size={14} /> {savingSid ? "Saving..." : "Save SID"}
          </button>
          {!subaccountSid.trim() ? (
            <button
              className="btn"
              type="button"
              onClick={() => void onCreateSubaccount()}
              disabled={creatingSub}
              title="Creates a Twilio subaccount under the master account and links it to this company"
            >
              <Plus size={14} /> {creatingSub ? "Creating..." : "Create subaccount"}
            </button>
          ) : null}
        </div>
      </div>

      {error ? <p className="error">{error}</p> : null}
      {agentsError ? (
        <p className="error">
          Couldn&apos;t load this tenant&apos;s sales reps: {agentsError}. The assign-to-agent picker
          needs the rep list — hit Refresh to retry.
        </p>
      ) : null}
      {loading ? <p className="muted">Loading numbers...</p> : null}

      {!loading && !voiceNumbers.length ? (
        <p className="muted" style={{ margin: 0 }}>
          No voice numbers yet. Buy the number(s) in the tenant&apos;s Twilio subaccount
          {tenant.twilioSubaccountSid ? ` (${tenant.twilioSubaccountSid})` : ""} and they&apos;re picked up
          here automatically, webhooks wired for you. Or hit &quot;Sync from Twilio&quot;.
        </p>
      ) : null}

      {voiceNumbers.length ? (
        <table className="table">
          <thead>
            <tr>
              <th>Number</th>
              <th>Assigned agent</th>
              <th>Agent mobile</th>
              <th style={{ width: 1 }}></th>
            </tr>
          </thead>
          <tbody>
            {voiceNumbers.map((n) => {
              const agent = agents.find((a) => a.id === n.assignedAgentId);
              return (
                <tr key={n.externalId}>
                  <td style={{ fontVariantNumeric: "tabular-nums" }}>{n.externalId}</td>
                  <td>
                    <select
                      value={n.assignedAgentId ?? ""}
                      disabled={busyNumber === n.externalId}
                      onChange={(e) => void onAssign(n, e.target.value)}
                      aria-label={`Assign ${n.externalId}`}
                    >
                      <option value="">— Unassigned —</option>
                      {agents.map((a) => (
                        <option key={a.id} value={a.id}>
                          {a.fullName}
                          {a.status !== "active" ? ` (${a.status})` : ""}
                        </option>
                      ))}
                    </select>
                  </td>
                  <td>
                    {agent?.phoneE164 ? (
                      <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
                        {agent.phoneE164}
                        {agent.phoneVerifiedAt ? (
                          <span title="Mobile verified by SMS OTP" style={{ color: "#16A34A", display: "inline-flex", alignItems: "center", gap: 2 }}>
                            <BadgeCheck size={13} /> verified
                          </span>
                        ) : (
                          <span className="muted">unverified</span>
                        )}
                      </span>
                    ) : (
                      <span className="muted">{agent ? "no mobile" : "—"}</span>
                    )}
                  </td>
                  <td>
                    <span style={{ display: "inline-flex", gap: 6, alignItems: "center" }}>
                      {n.assignedAgentId ? (
                        <button
                          className="btn ghost"
                          title="Unassign this number"
                          disabled={busyNumber === n.externalId}
                          onClick={() => void onAssign(n, "")}
                        >
                          <Link2Off size={14} />
                        </button>
                      ) : (
                        <Link2 size={14} className="muted" aria-hidden />
                      )}
                      {agent ? (
                        agent.callerIdVerified ? (
                          <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
                            <span
                              title="Customers see this rep's own number on calls"
                              style={{ color: "#16A34A", display: "inline-flex", alignItems: "center", gap: 3, fontSize: 12 }}
                            >
                              <BadgeCheck size={13} /> caller ID
                            </span>
                            <button
                              className="btn ghost"
                              title="Reset verification so you can run the Verify flow again (does not touch purchased numbers)"
                              disabled={resetting === agent.id}
                              onClick={() => void onResetCallerId(agent)}
                            >
                              <RotateCcw size={13} /> {resetting === agent.id ? "Resetting..." : "Reset"}
                            </button>
                          </span>
                        ) : (
                          <button
                            className="btn ghost"
                            title="Verify this rep's number with Twilio so customers see it instead of the Twilio number"
                            disabled={verifying === agent.id}
                            onClick={() => void onVerifyCallerId(agent)}
                          >
                            {verifying === agent.id ? "Verifying..." : "Verify caller ID"}
                          </button>
                        )
                      ) : null}
                      <button
                        className="btn ghost"
                        title="Remove this number from the tenant (does not release it on Twilio)"
                        disabled={busyNumber === n.externalId}
                        onClick={() => void onDelete(n)}
                      >
                        <Trash2 size={14} />
                      </button>
                    </span>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      ) : null}

      {otherEndpoints.length ? (
        <div>
          <h4 style={{ margin: "8px 0 4px" }}>Other channels</h4>
          <p className="muted" style={{ margin: "0 0 8px" }}>WhatsApp / messaging senders on this tenant.</p>
          <table className="table">
            <thead>
              <tr>
                <th>Channel</th>
                <th>Endpoint</th>
                <th>Assigned agent</th>
              </tr>
            </thead>
            <tbody>
              {otherEndpoints.map((n) => (
                <tr key={n.channel + n.externalId}>
                  <td>{n.channel}</td>
                  <td style={{ fontVariantNumeric: "tabular-nums" }}>{n.externalId}</td>
                  <td>{n.assignedAgentName ?? <span className="muted">—</span>}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : null}

      <div style={{ borderTop: "1px solid var(--border, rgba(255,255,255,0.08))", paddingTop: 16, display: "grid", gap: 8 }}>
        <h4 style={{ margin: 0, display: "flex", alignItems: "center", gap: 8 }}>
          <MessageCircle size={15} /> WhatsApp (Coexistence)
        </h4>
        <p className="muted" style={{ margin: 0 }}>
          Connect a sales agent&apos;s existing WhatsApp Business App number through Meta&apos;s Embedded
          Signup. The rep keeps WhatsApp on their phone; we just start receiving and sending alongside it.
        </p>

        {waIntegrations.length ? (
          <table className="table" style={{ marginTop: 4 }}>
            <thead>
              <tr>
                <th>WhatsApp number</th>
                <th>Assigned agent</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {waIntegrations.map((w) => {
                const agent = agents.find((a) => a.id === w.assignedAgentId);
                return (
                  <tr key={w.phoneNumberId}>
                    <td style={{ fontVariantNumeric: "tabular-nums" }}>{w.displayPhoneNumber}</td>
                    <td>{agent?.fullName ?? <span className="muted">—</span>}</td>
                    <td>
                      <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
                        {w.status === "connected" || w.status === "active" ? (
                          <span style={{ color: "#16A34A", display: "inline-flex", alignItems: "center", gap: 4 }}>
                            <BadgeCheck size={13} /> {w.status}
                          </span>
                        ) : (
                          <span className="muted">{w.status}</span>
                        )}
                      </span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        ) : (
          <p className="muted" style={{ margin: 0 }}>No WhatsApp numbers connected yet.</p>
        )}

        <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
          <select
            value={waAgentId}
            disabled={!whatsappConfigured || connectingWa || !agents.length}
            onChange={(e) => setWaAgentId(e.target.value)}
            aria-label="Sales agent to connect WhatsApp for"
            style={{ minWidth: 220 }}
          >
            <option value="">— Choose sales agent —</option>
            {agents.map((a) => (
              <option key={a.id} value={a.id}>
                {a.fullName}
                {a.phoneE164 ? ` (${a.phoneE164})` : ""}
              </option>
            ))}
          </select>
          <button
            className="btn"
            type="button"
            onClick={() => void onConnectWhatsApp()}
            disabled={!whatsappConfigured || connectingWa || !waAgentId}
            title={
              whatsappConfigured
                ? "Launch Meta's Embedded Signup for the selected agent"
                : "WhatsApp not configured yet"
            }
          >
            <MessageCircle size={14} /> {connectingWa ? "Connecting..." : "Connect WhatsApp"}
          </button>
          {!whatsappConfigured ? (
            <span className="muted" style={{ fontSize: 12 }}>
              WhatsApp not configured yet — set VITE_META_APP_ID and VITE_META_ES_CONFIG_ID.
            </span>
          ) : !agents.length ? (
            <span className="muted" style={{ fontSize: 12 }}>
              No active sales agents to connect — add an agent first.
            </span>
          ) : null}
        </div>
      </div>
    </div>
  );
}
